import { useCallback } from "react";
import { Api } from "../service/Api";
import { useDataRequest } from "./api";
import { OrganizationPublicId } from "../service/server/Types";
import { OrganizationDto } from "../service/server/dto/OrganizationDto";
import { NetworkRequestState } from "../models";
import { SurveyAnswers } from "./api/useSurvey";
import { DevInstructionsPoliciesAndNoticesOnboardingSlug } from "../../polaris/pages/stay-compliant/privacy-center/DSARForms";

export type TruevaultPrivacyCenterOnboardingProgress = {
  isFinishedWithPoliciesNotices: boolean;
  isWebsiteConsentSetup: boolean;
  isDevInstructionsFinished: boolean;
  isBiometricIdentifiersPolicyFinished: boolean;
};

export type TruevaultHrOnboardingProgress = {
  isFinishedWithHrDataMap: boolean;
  isFinishedWithHrRetentionPolicy: boolean;
  isHrNoticesPublished: boolean;
};

export type TruevaultWorkflowsOnboardingProgress = {
  isFinishedWithApiIntegrationsSetup: boolean;
  isFinishedDataStorageOnboarding: boolean;
  isFinishedWorkflowSetupOnboarding: boolean;
  isLimitSettingsComplete: boolean;
};

export type TruevaultDataMapOnboardingProgress = {
  allVendorsComplete: boolean;
  isDataRetentionComplete: boolean;
};

export type TruevaultSurveyOnboardingProgress = {
  isBusinessSurveyFinished: boolean;
  isHrSurveyFinished: boolean;
};

export type TruevaultOnboardingProgress = {
  privacyCenterOnboardingProgress: TruevaultPrivacyCenterOnboardingProgress;
  hrCenterOnboardingProgress: TruevaultHrOnboardingProgress;
  workflowsSetupOnboardingProgress: TruevaultWorkflowsOnboardingProgress;
  dataMapOnboardingProgress: TruevaultDataMapOnboardingProgress;
  surveyOnboardingProgress: TruevaultSurveyOnboardingProgress;
};

/*
 * Returns true if all onboarding steps are complete
 */
export const isAllOnboardingStepsComplete = (
  organization: OrganizationDto,
  onboardingProgress?: TruevaultOnboardingProgress,
) => {
  let isHrCenterSetupRequired = organization?.requiresEmployeeMapping === true;

  if (!onboardingProgress) {
    return false;
  }

  return (
    isPrivacyCenterOnboardingComplete(onboardingProgress.privacyCenterOnboardingProgress) &&
    (!isHrCenterSetupRequired ||
      isHrCenterOnboardingComplete(onboardingProgress.hrCenterOnboardingProgress)) &&
    isWorkflowsOnboardingComplete(onboardingProgress.workflowsSetupOnboardingProgress)
  );
};

export const isPrivacyCenterOnboardingComplete = (
  privacyCenterOnboarding?: TruevaultPrivacyCenterOnboardingProgress,
  onlyEssentialSteps = false,
) => {
  if (!privacyCenterOnboarding) {
    return false;
  }

  return (
    privacyCenterOnboarding.isFinishedWithPoliciesNotices &&
    (onlyEssentialSteps || privacyCenterOnboarding.isWebsiteConsentSetup) &&
    privacyCenterOnboarding.isDevInstructionsFinished
  );
};

export const didFinishPoliciesAndNoticesOnboarding = (
  developerInstructionSurveyAnswers: SurveyAnswers,
) => {
  return (
    developerInstructionSurveyAnswers &&
    developerInstructionSurveyAnswers[DevInstructionsPoliciesAndNoticesOnboardingSlug] === "true"
  );
};

export const isHrCenterOnboardingComplete = (
  hrCenterOnboarding?: TruevaultHrOnboardingProgress,
) => {
  if (!hrCenterOnboarding) {
    return false;
  }

  return (
    hrCenterOnboarding.isFinishedWithHrDataMap &&
    hrCenterOnboarding.isFinishedWithHrRetentionPolicy &&
    hrCenterOnboarding.isHrNoticesPublished
  );
};

// Note that we do *not* check for isLimitSettingsComplete here, since it's not a one-time onboarding setup step
export const isWorkflowsOnboardingComplete = (
  workflowsOnboarding?: TruevaultWorkflowsOnboardingProgress,
) => {
  if (!workflowsOnboarding) {
    return false;
  }

  return (
    workflowsOnboarding.isFinishedDataStorageOnboarding &&
    workflowsOnboarding.isFinishedWithApiIntegrationsSetup &&
    workflowsOnboarding.isFinishedWorkflowSetupOnboarding
  );
};

export const useOnboardingProgress = (
  organizationId: OrganizationPublicId,
): [TruevaultOnboardingProgress, NetworkRequestState] => {
  const apiRequest = useCallback(
    () => Api.organization.getOrganizationOnboardingProgress(organizationId),
    [organizationId],
  );

  const { result: onboardingProgress, request: onboardingProgressRequest } = useDataRequest({
    queryKey: ["orgOnboardingProgress", organizationId],
    api: apiRequest,
    notReady: !organizationId,
  });

  return [onboardingProgress, onboardingProgressRequest];
};
